import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : 'https://apiqldangkyxe.nguyenluan.vn/api';

// Tạo axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 giây mặc định
});

// Interceptor để thêm token vào header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor để xử lý response
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Xử lý lỗi 401 (Unauthorized) - token hết hạn hoặc không hợp lệ
    if (error.response?.status === 401) {
      const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
        error.config?.url?.includes('/auth/register');

      if (!isAuthEndpoint) {
        // Chỉ xử lý khi đã có token (đã đăng nhập trước đó)
        const hasToken = localStorage.getItem('token');
        if (hasToken) {

          localStorage.removeItem('token');
          localStorage.removeItem('user');

          // Dispatch custom event để AuthContext có thể cập nhật state
          window.dispatchEvent(new CustomEvent('auth-logout'));

          // Không redirect tự động, để AuthContext xử lý
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
  // Admin user management
  getAllUsers: () => api.get('/auth/users'),
  createUser: (userData) => api.post('/auth/users', userData),
  updateUser: (userId, userData) => api.put(`/auth/users/${userId}`, userData),
  toggleUserActive: (userId, isActive) => api.patch(`/auth/users/${userId}/toggle-active`, { isActive }),
  deleteUser: (userId) => api.delete(`/auth/users/${userId}`),
};

// Data API (legacy)
export const dataAPI = {
  getAllData: (params) => api.get('/data', { params }),
  getDataById: (id) => api.get(`/data/${id}`),
  createData: (data) => api.post('/data', data),
  updateData: (id, data) => api.put(`/data/${id}`, data),
  deleteData: (id) => api.delete(`/data/${id}`),
};

// Vehicle Registration API
export const vehicleRegistrationAPI = {
  getUnits: () => api.get('/vehicle-registration/units'),
  getUnitsPublic: () => api.get('/vehicle-registration/units/public'),
  getBusinessTypes: () => api.get('/vehicle-registration/business-types'),
  getBusinessTypesWithVehicles: () => api.get('/vehicle-registration/business-types-with-vehicles'),
  getVehicleTypes: () => api.get('/vehicle-registration/vehicle-types'),
  getVehicleTypesByBusiness: (businessTypeId) => api.get(`/vehicle-registration/business-types/${businessTypeId}/vehicle-types`),
  getRegistrations: (params) => api.get('/vehicle-registration/daily-records', { params }),
  upsertDailyRecord: (data) => api.post('/vehicle-registration/daily-records', data),
  getReportData: (params) => api.get('/vehicle-registration/report-data', { params }),
  reorderUnits: (unitIds) => api.put('/vehicle-registration/units/reorder', { unitIds }),
};

// Document API
export const documentAPI = {
  // Admin functions
  getDocuments: (params) => api.get('/documents', { params }),
  getMyDocuments: (params) => api.get('/documents/my', { params }),
  getUnitDocuments: (params) => api.get('/documents/from-units', { params }),
  uploadDocument: (formData) => api.post('/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  deleteDocument: (id) => api.delete(`/documents/${id}`),
  generateQRCode: (id) => api.get(`/documents/${id}/qr`),
  getUnitsForDocument: () => api.get('/documents/units'),

  // Public functions (with optional auth for permission filtering)
  getPublicDocuments: (params) => api.get('/documents/public', { params }),
  getDocumentByQR: (qrCode) => axios.get(`${API_BASE_URL}/documents/public/${qrCode}`),
  downloadDocumentByQR: (qrCode) => axios.get(`${API_BASE_URL}/documents/public/${qrCode}/download`, {
    responseType: 'blob'
  }),
};

// Unit Permission API
export const unitPermissionAPI = {
  // Lấy danh sách tất cả supervisors với phân quyền
  getAllSupervisorsWithPermissions: () => api.get('/unit-permissions/supervisors'),

  // Lấy phân quyền của một supervisor cụ thể
  getSupervisorPermissions: (supervisorId) => api.get(`/unit-permissions/supervisors/${supervisorId}`),

  // Cập nhật phân quyền cho supervisor
  updateSupervisorPermissions: (supervisorId, data) => api.put(`/unit-permissions/supervisors/${supervisorId}`, data),

  // Thêm phân quyền đơn lẻ
  addPermission: (data) => api.post('/unit-permissions/permissions', data),

  // Xóa phân quyền cụ thể
  removePermission: (permissionId) => api.delete(`/unit-permissions/permissions/${permissionId}`)
};

// Vehicle Details API
export const vehicleDetailAPI = {
  importLicensePlates: (formData) => api.post('/vehicle-details/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  getVehicleDetails: (params) => api.get('/vehicle-details', { params }),
  getAllVehicleDetailsForExport: (params) => api.get('/vehicle-details/export-all', { params }),
  exportExcelWithPhotos: (params) => api.get('/vehicle-details/export-excel', { 
    params,
    responseType: 'blob', // Quan trọng: nhận file binary
    timeout: 900000 // 15 phút timeout cho export Excel với ảnh
  }),
  getVehicleDetailById: (id) => api.get(`/vehicle-details/${id}`),
  updateVehicleDetail: (id, data) => api.put(`/vehicle-details/${id}`, data),
  deleteVehicleDetail: (id) => api.delete(`/vehicle-details/${id}`),
  getVehicleStatistics: (params) => api.get('/vehicle-details/statistics', { params }),
  getUnitsWithCompletionStatus: (params) => api.get('/vehicle-details/units/completion-status', { params }),
  // Upload ảnh phương tiện (có thể upload nhiều ảnh)
  uploadVehiclePhotos: (id, formData) => api.post(`/vehicle-details/${id}/photos`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  getVehiclePhotos: (id) => api.get(`/vehicle-details/${id}/photos`),
  getVehiclePhotoBase64: (id, photoId) => api.get(`/vehicle-details/${id}/photos/${photoId}/base64`),
  deleteVehiclePhoto: (id, photoId) => api.delete(`/vehicle-details/${id}/photos/${photoId}`),
};

// License Plate Report API
export const licensePlateReportAPI = {
  exportWordReport: (params) => api.get('/license-plate-report/export-word', {
    params,
    responseType: 'blob',
    timeout: 300000 // 5 phút
  })
};

// Report Review API
export const reportReviewAPI = {
  getStatistics: (params) => api.get('/report-reviews/statistics', { params }),
  getUnitsWithCompletionStatus: (params) => api.get('/report-reviews/units/completion-status', { params }),
  getReports: (params) => api.get('/report-reviews', { params }),
  getReportById: (id) => api.get(`/report-reviews/${id}`),
  createReport: (data) => api.post('/report-reviews', data),
  updateReport: (id, data) => api.put(`/report-reviews/${id}`, data),
  deleteReport: (id) => api.delete(`/report-reviews/${id}`),
  importExcel: (formData) => api.post('/report-reviews/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

// Vehicle Statistic API
export const vehicleStatisticAPI = {
  getStatistics: (params) => api.get('/vehicle-statistics', { params }),
  getStatisticById: (id) => api.get(`/vehicle-statistics/${id}`),
  getUnitsWithCompletionStatus: (params) => api.get('/vehicle-statistics/units/completion-status', { params }),
  createStatistic: (data) => {
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return api.post('/vehicle-statistics', data, config);
  },
  updateStatistic: (id, data) => {
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return api.put(`/vehicle-statistics/${id}`, data, config);
  },
  deleteStatistic: (id) => api.delete(`/vehicle-statistics/${id}`),
  importStatistics: (formData) => api.post('/vehicle-statistics/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  exportExcel: (params) => api.get('/vehicle-statistics/export', {
    params,
    responseType: 'blob',
    timeout: 300000 // 5 phút timeout cho export
  })
};

// Special Moto (Xe Máy Chuyên Dùng) API
export const specialMotoAPI = {
  getAll: (params) => api.get('/special-motos', { params }),
  getById: (id) => api.get(`/special-motos/${id}`),
  create: (data) => api.post('/special-motos', data),
  update: (id, data) => api.put(`/special-motos/${id}`, data),
  remove: (id) => api.delete(`/special-motos/${id}`),
  importExcel: (formData) => api.post('/special-motos/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  exportExcel: (params) => api.get('/special-motos/export', {
    params,
    responseType: 'blob',
    timeout: 300000
  }),
  getSupervisorUnits: () => api.get('/special-motos/supervisor-units')
};

// Vehicle Registration Report (Báo cáo ĐKX) API
export const vehicleRegistrationReportAPI = {
  getAll: (params) => api.get('/vehicle-registration-reports', { params }),
  getById: (id) => api.get(`/vehicle-registration-reports/${id}`),
  create: (data) => api.post('/vehicle-registration-reports', data),
  update: (id, data) => api.put(`/vehicle-registration-reports/${id}`, data),
  delete: (id) => api.delete(`/vehicle-registration-reports/${id}`),
  exportExcel: (params) => api.get('/vehicle-registration-reports/export', {
    params,
    responseType: 'blob',
    timeout: 300000
  })
};

export default api;
